/**
 * BottomNavigation 번역 상수
 */
import { type BottomNavigationLanguage } from './types';
import { TAB_ROUTES } from '@/config/routes';

export interface BottomNavigationTranslations {
  cart: string;
  pay: string;
  tabs: {
    home: string;
    orders: string;
    qr: string;
    points: string;
    admin: string;
  };
}

export const translations: Record<BottomNavigationLanguage, BottomNavigationTranslations> = {
  ko: {
    cart: '장바구니',
    pay: '결제하기',
    tabs: {
      home: '홈',
      orders: '주문',
      qr: 'QR 코드',
      points: '포인트',
      admin: '관리',
    },
  },
  es: {
    cart: 'Carrito',
    pay: 'Pagar',
    tabs: {
      // #393 Stage 4b: 잔존 영어 키 → es-MX (HOME / ORDERS / QR CODE / ADMIN)
      // - "QR" → "CÓDIGO QR" (메타 #380 의 "QR CODE" 직역)
      // - "ADMIN" → "CUENTA" (라우트 /admin = 사용자 계정 페이지: Profile/OrderHistory/PaymentMethod/FAQ
      //   참조 → 관리자 페이지가 아님. 한국어 "관리" 와 일관)
      home: 'INICIO',
      orders: 'PEDIDOS',
      qr: 'CÓDIGO QR',
      points: 'PUNTOS',
      admin: 'CUENTA',
    },
  },
  en: {
    cart: 'Cart',
    pay: 'Pay',
    tabs: {
      home: 'HOME',
      orders: 'ORDERS',
      qr: 'QR CODE',
      points: 'POINTS',
      admin: 'ACCOUNT',
    },
  },
  // #512 — 12개 언어 추가 (Tier 2~5). 본 세션 직접 번역 + codex follow-up 검수 (사용자 결정).
  // tab admin = ACCOUNT (사용자 계정 페이지: Profile/OrderHistory/PaymentMethod/FAQ).
  pt: {
    cart: 'Carrinho',
    pay: 'Pagar',
    tabs: {
      home: 'INÍCIO',
      orders: 'PEDIDOS',
      qr: 'CÓDIGO QR',
      points: 'PONTOS',
      admin: 'CONTA',
    },
  },
  fr: {
    cart: 'Panier',
    pay: 'Payer',
    tabs: {
      home: 'ACCUEIL',
      orders: 'COMMANDES',
      qr: 'CODE QR',
      points: 'POINTS',
      admin: 'COMPTE',
    },
  },
  zh: {
    cart: '购物车',
    pay: '支付',
    tabs: {
      home: '首页',
      orders: '订单',
      qr: '二维码',
      points: '积分',
      admin: '账户',
    },
  },
  ja: {
    cart: 'カート',
    pay: '支払う',
    tabs: {
      home: 'ホーム',
      orders: '注文',
      qr: 'QRコード',
      points: 'ポイント',
      admin: 'アカウント',
    },
  },
  hi: {
    cart: 'कार्ट',
    pay: 'भुगतान',
    tabs: {
      home: 'होम',
      orders: 'ऑर्डर',
      qr: 'क्यूआर कोड',
      points: 'अंक',
      admin: 'खाता',
    },
  },
  de: {
    cart: 'Warenkorb',
    pay: 'Bezahlen',
    tabs: {
      home: 'START',
      orders: 'BESTELLUNGEN',
      qr: 'QR-CODE',
      points: 'PUNKTE',
      admin: 'KONTO',
    },
  },
  it: {
    cart: 'Carrello',
    pay: 'Paga',
    tabs: {
      home: 'HOME',
      orders: 'ORDINI',
      qr: 'CODICE QR',
      points: 'PUNTI',
      admin: 'ACCOUNT',
    },
  },
  ru: {
    cart: 'Корзина',
    pay: 'Оплатить',
    tabs: {
      home: 'ГЛАВНАЯ',
      orders: 'ЗАКАЗЫ',
      qr: 'QR-КОД',
      points: 'БАЛЛЫ',
      admin: 'АККАУНТ',
    },
  },
  ar: {
    cart: 'السلة',
    pay: 'الدفع',
    tabs: {
      home: 'الرئيسية',
      orders: 'الطلبات',
      qr: 'رمز الاستجابة',
      points: 'النقاط',
      admin: 'الحساب',
    },
  },
  vi: {
    cart: 'Giỏ hàng',
    pay: 'Thanh toán',
    tabs: {
      home: 'TRANG CHỦ',
      orders: 'ĐƠN HÀNG',
      qr: 'MÃ QR',
      points: 'ĐIỂM',
      admin: 'TÀI KHOẢN',
    },
  },
  th: {
    cart: 'ตะกร้า',
    pay: 'ชำระเงิน',
    tabs: {
      home: 'หน้าแรก',
      orders: 'คำสั่งซื้อ',
      qr: 'คิวอาร์โค้ด',
      points: 'คะแนน',
      admin: 'บัญชี',
    },
  },
  nl: {
    cart: 'Winkelwagen',
    pay: 'Betalen',
    tabs: {
      home: 'HOME',
      orders: 'BESTELLINGEN',
      qr: 'QR-CODE',
      points: 'PUNTEN',
      admin: 'ACCOUNT',
    },
  },
};

export function resolveBottomNavigationLanguage(
  locale: string | null | undefined,
): BottomNavigationLanguage {
  return locale && Object.prototype.hasOwnProperty.call(translations, locale)
    ? (locale as BottomNavigationLanguage)
    : 'es';
}

export function getBottomNavigationTranslations(
  locale: string | null | undefined,
): BottomNavigationTranslations {
  return translations[resolveBottomNavigationLanguage(locale)];
}

/**
 * 라우트 매핑
 */
export { TAB_ROUTES };
